import { Timestamp } from '@lib/graphql/__generated__/graphql'
import { cache } from '@lib/graphql/cache'
import { GET_USER_ACCOUNT } from '@lib/graphql/queries/Organization'
import { formatDate as fd } from '@mono/packages/utils/date-utils'
import { TFunction } from '@translations/useTranslations'
import {
  format as dateFNSFormatDate,
  differenceInDays,
  differenceInMinutes,
  isAfter,
  isBefore,
  isSameDay,
  isToday,
  subDays,
} from 'date-fns'
import { DEFAULT_LOCALE, EN_LOCALE } from './i18n'

export interface TimeStamp extends Timestamp {}

export const dateToUTCSeconds = (date: Date) =>
  Math.round(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    ) / 1000,
  )

const dateToSeconds = (date: Date) => Math.round(date.getTime() / 1000)

export const getTimeStamp: (date: Date, utc?: boolean) => TimeStamp = (
  date,
  utc,
) => ({
  seconds: utc ? dateToUTCSeconds(date) : dateToSeconds(date),
})

export const getNanos = (date: Date) => (date.getMilliseconds() ?? 0) * 1e6

export const createTimeStamp = (values: {
  seconds: number
  nanos?: number
}) => ({
  seconds: values.seconds,
  nanos: values.nanos,
})

export const isAfterTimestamp = (ts1: Timestamp, ts2: Timestamp): boolean =>
  ts1?.seconds !== ts2?.seconds
    ? (ts1?.seconds ?? 0) > (ts2?.seconds ?? 0)
    : (ts1?.nanos ?? 0) > (ts2?.nanos ?? 0)

export const getDateFromTimeStamp = (
  timeStamp: TimeStamp | Timestamp,
  utc?: boolean,
) => {
  if (utc) {
    return getDateFromUTCTimeStamp(timeStamp)
  }

  return new Date(timeStamp.seconds * 1000)
}

const getDateFromUTCTimeStamp = (timeStamp: TimeStamp | Timestamp) => {
  const d = new Date(timeStamp?.seconds * 1000)
  const utcDate = new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds(),
  )

  return utcDate
}

export const getStartOfDay = (date: string | number | Date) => {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)

  return d
}

export const getStartOfMonth = (date: string | number | Date) => {
  const d = new Date(date)
  d.setDate(1)
  d.setHours(0, 0, 0, 0)

  return d
}

export const getEndOfDay = (date: string | number | Date) => {
  const d = new Date(date)
  d.setHours(23, 59, 59, 59)

  return d
}

export const getDaysDifference = (a: Date, b: Date) => {
  return Math.round((a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24))
}

export const isValidDate = (timeStamp: TimeStamp | Timestamp | undefined) =>
  timeStamp?.seconds > 0

export const formatDate = (
  timeStamp: TimeStamp | Timestamp | undefined,
  withTime?: boolean,
  utc: boolean = !withTime,
) => {
  const data = cache.readQuery({
    query: GET_USER_ACCOUNT,
  })

  return fd({
    timeStamp,
    withTime,
    utc,
    dateFormatPreferences: data?.getCurrentUserAccount?.datePreferences,
  })
}

export const formatDateRelative = ({
  date,
  withTime,
  t,
}: {
  date: TimeStamp
  withTime?: boolean
  t: TFunction
}) => {
  const notificationDate = getDateFromTimeStamp(date)
  const diffMinutes = differenceInMinutes(new Date(), notificationDate)

  const data = cache.readQuery({
    query: GET_USER_ACCOUNT,
  })

  const lang = (data as any)?.getCurrentUserAccount?.lang ?? DEFAULT_LOCALE

  if (diffMinutes <= 0) {
    return t('expressions.justNow')
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} ${
      diffMinutes > 1 ? t('expressions.minutes') : t('expressions.minute')
    } ${lang === EN_LOCALE ? 'ago' : ''}`
  }

  if (isToday(notificationDate)) {
    return `${t('expressions.today')}, ${dateFNSFormatDate(
      getDateFromTimeStamp(date),
      'HH:mm',
    )}`
  }

  return formatDate(date, withTime)
}

export const getBrowserTimezone = () =>
  Intl.DateTimeFormat()?.resolvedOptions()?.timeZone ?? 'Europe/Sofia'

export const isBetween = (date: Date, { from, to }: { from: Date; to: Date }) =>
  (isAfter(date, from) && isBefore(date, to)) ||
  isSameDay(date, from) ||
  isSameDay(date, to)

export const getPreviousPeriod = (
  fromTimestamp: TimeStamp | Timestamp,
  toTimestamp: TimeStamp | Timestamp,
  utc?: boolean,
): { from: Date; to: Date } => {
  const currentPeriodStart = getDateFromTimeStamp(fromTimestamp, utc)
  const currentPeriodEnd = getDateFromTimeStamp(toTimestamp, utc)
  const periodDuration = differenceInDays(currentPeriodEnd, currentPeriodStart)

  const previousPeriodEnd = subDays(currentPeriodStart, 1)
  const previousPeriodStart = subDays(previousPeriodEnd, periodDuration)

  return {
    from: previousPeriodStart,
    to: previousPeriodEnd,
  }
}
